import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Select, Modal } from './Common';

/**
 * Slack and Teams destinations for one scope — a client, or the MSP itself.
 *
 * Channels are per-client: a notification only ever reaches the channel
 * belonging to the recipient's own client, so each client configures its own
 * here rather than sharing one provider-wide destination.
 *
 * `scope` is a client id, or the string 'msp' for the provider's own channels.
 */

const CHANNEL_TYPES = [
  {
    value: 'slack',
    label: 'Slack',
    colour: '#4A154B',
    help: 'In Slack: Apps → Incoming Webhooks → Add to Slack, pick the channel, then copy the webhook URL.',
    placeholder: 'https://hooks.slack.com/services/T000/B000/xxxx',
  },
  {
    value: 'teams',
    label: 'Microsoft Teams',
    colour: '#4B53BC',
    help: 'In Teams: open the channel → Workflows → "Post to a channel when a webhook request is received", then copy the HTTP POST URL. The older Office 365 connector webhooks are retired and will not work.',
    placeholder: 'https://prod-00.westus.logic.azure.com:443/workflows/...?api-version=...&sig=...',
  },
];

export default function NotificationChannels({ scope, scopeLabel }) {
  const { apiFetch } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    if (!scope) { setChannels([]); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/notifications/channels?clientId=${encodeURIComponent(scope)}`);
      const data = await res.json().catch(() => null);
      setChannels(Array.isArray(data) ? data.filter(c => c.channel_type !== 'in_app') : []);
    } catch {
      setChannels([]);
    }
    setLoading(false);
  }, [apiFetch, scope]);

  useEffect(() => { load(); }, [load]);

  const openAdd = (type) => {
    setEditing(null);
    setForm({ channel_type: type, name: `${CHANNEL_TYPES.find(t => t.value === type).label} notifications`, webhook_url: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (channel) => {
    setEditing(channel);
    // The URL is never returned by the API, so this starts blank: leaving it
    // blank keeps the stored one, typing a new one replaces it.
    setForm({ channel_type: channel.channel_type, name: channel.name, webhook_url: '' });
    setError('');
    setShowModal(true);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const body = { name: form.name };
    if (form.webhook_url) body.webhook_url = form.webhook_url.trim();
    let res;
    if (editing) {
      res = await apiFetch(`/notifications/channels/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      res = await apiFetch('/notifications/channels', {
        method: 'POST',
        body: JSON.stringify({
          ...body,
          channel_type: form.channel_type,
          client_id: scope === 'msp' ? null : scope,
        }),
      });
    }
    setSaving(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || 'Could not save the channel');
      return;
    }
    setShowModal(false);
    load();
  };

  const remove = async (channel) => {
    if (!window.confirm(`Delete the ${channel.name} channel? Notifications will stop being sent to it.`)) return;
    const res = await apiFetch(`/notifications/channels/${channel.id}`, { method: 'DELETE' });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d.error || 'Could not delete the channel');
      return;
    }
    load();
  };

  const toggleActive = async (channel) => {
    await apiFetch(`/notifications/channels/${channel.id}`, {
      method: 'PUT', body: JSON.stringify({ is_active: !channel.is_active }),
    });
    load();
  };

  const test = async (channel) => {
    setTesting(channel.id);
    setTestResult(null);
    const res = await apiFetch(`/notifications/channels/${channel.id}/test`, { method: 'POST' });
    const d = await res.json().catch(() => ({}));
    setTesting(null);
    setTestResult({
      id: channel.id,
      ok: res.ok,
      message: res.ok ? 'Test message sent — check the channel.' : (d.error || 'The channel did not accept the message.'),
    });
  };

  if (!scope) return null;

  const configured = new Set(channels.map(c => c.channel_type));
  const typeInfo = (t) => CHANNEL_TYPES.find(x => x.value === t) || { label: t, colour: '#6b7280' };

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Notification Channels</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          Where {scopeLabel || 'this client'}'s notifications are posted. Each channel receives only
          this {scope === 'msp' ? 'organisation' : 'client'}'s notifications, and users choose which
          events reach them in their own notification settings.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {CHANNEL_TYPES.filter(t => !configured.has(t.value)).map(t => (
          <Button key={t.value} onClick={() => openAdd(t.value)}
            style={{ background: t.colour, color: '#fff', border: 'none' }}>
            + Add {t.label}
          </Button>
        ))}
        {configured.size === CHANNEL_TYPES.length && (
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', alignSelf: 'center' }}>
            Slack and Teams are both configured.
          </span>
        )}
      </div>

      {loading ? (
        <Card style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Loading channels...</Card>
      ) : channels.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-secondary)' }}>
          No channels yet. Add Slack or Teams above to have notifications posted there.
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {channels.map(c => (
            <Card key={c.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                  background: typeInfo(c.channel_type).colour, color: '#fff',
                }}>{typeInfo(c.channel_type).label}</span>
                <strong style={{ fontSize: 15 }}>{c.name}</strong>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                  background: c.is_active ? '#22c55e' : 'var(--bg-input)',
                  color: c.is_active ? '#fff' : 'var(--text-secondary)',
                  border: c.is_active ? 'none' : '1px solid var(--border)',
                }}>{c.is_active ? 'Active' : 'Paused'}</span>
                {!c.has_webhook && (
                  <span style={{ fontSize: 12, color: '#dc2626' }}>No webhook URL set</span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                    disabled={testing === c.id || !c.has_webhook}
                    onClick={() => test(c)}>{testing === c.id ? 'Sending...' : 'Send test'}</Button>
                  <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                    onClick={() => toggleActive(c)}>{c.is_active ? 'Pause' : 'Resume'}</Button>
                  <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                    onClick={() => openEdit(c)}>Edit</Button>
                  <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
                    onClick={() => remove(c)}>Delete</Button>
                </div>
              </div>
              {testResult && testResult.id === c.id && (
                <div style={{
                  marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 13,
                  background: testResult.ok ? '#f0fdf4' : '#fef2f2',
                  color: testResult.ok ? '#16a34a' : '#dc2626',
                  border: `1px solid ${testResult.ok ? '#bbf7d0' : '#fecaca'}`,
                }}>{testResult.message}</div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={`${editing ? 'Edit' : 'Add'} ${typeInfo(form.channel_type).label} channel`} width={620} noOverlayClose>
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626',
                        border: '1px solid #fecaca', marginBottom: 16, fontSize: 14 }}>{error}</div>
        )}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.55 }}>
          {typeInfo(form.channel_type).help}
        </div>
        <Input label="Name" value={form.name || ''}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <Input label={editing ? 'Webhook URL (leave blank to keep the current one)' : 'Webhook URL'}
          value={form.webhook_url || ''} placeholder={typeInfo(form.channel_type).placeholder}
          onChange={e => setForm(f => ({ ...f, webhook_url: e.target.value }))} />
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          The URL is stored encrypted and is never shown again after saving. Treat it as a password:
          anyone holding it can post into the channel.
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.name || (!editing && !form.webhook_url)}>
            {saving ? 'Saving...' : 'Save channel'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
